/**
 * ScorecardFilterPanel — compact, stacked expandable boxes for MarketView sidebar.
 *
 * Reads from a program's scorecards and renders scorecard items as filter controls.
 * Threshold edits (drag) are persisted to the backend via onItemUpdate (debounced),
 * which triggers ScorecardEditLog entries and scorecard.updated_at propagation.
 */
import React, { useState, useCallback, useRef } from 'react'
import { ChevronDown, ChevronRight, SlidersHorizontal, Plus } from 'lucide-react'
import NumericCriteriaSlider from '../marketview/NumericCriteriaSlider'
import type { SpecCriterion } from '../marketview/SpecCriteriaPanel'

/** Debounce a callback — only fires after `delay` ms of inactivity. */
function useDebouncedCallback<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): T {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  return useCallback(
    ((...args: any[]) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => fn(...args), delay)
    }) as any,
    [fn, delay]
  )
}

// ── Types ──────────────────────────────────────────────────────────

interface OrderedLevel {
  id: number
  value: string
  label: string
  order: number
}

interface FilterItem {
  id: number
  title: string
  description: string
  importance: 'nice_to_have' | 'important' | 'mission_critical'
  spec_id: number | null
  spec_type: string | null  // 'numeric', 'boolean', 'categorical', 'ordered_categorical'
  spec_unit: string | null
  typical_min: number | null
  typical_max: number | null
  lower_threshold: number | null
  upper_threshold: number | null
  higher_is_better: boolean  // from Spec
  section_id: number         // needed for POST when adding items
  spec_categories: any[]     // from Spec.category_options
  acceptable_categories: string[]  // user's current categorical selections
  // Ordered categorical
  spec_ordered_levels?: OrderedLevel[]
  minimum_acceptable_level?: number | null
  desirable_level?: number | null
}

interface FilterSection {
  id: number
  title: string
}

interface FilterScorecard {
  id: number
  name: string
  scorecard_type: string
  scorecard_type_display: string
  items: FilterItem[]
  sections: FilterSection[]
}

export interface ScorecardFilterCriteria {
  [specId: string]: {
    active: boolean
    importance: string
    lower_threshold?: number
    upper_threshold?: number
    required?: boolean
  }
}

interface ScorecardFilterPanelProps {
  scorecards: FilterScorecard[]
  criteria: ScorecardFilterCriteria
  onCriteriaChange: (criteria: ScorecardFilterCriteria) => void
  /** Called when a threshold is changed via drag — persists edit to the backend */
  onItemUpdate?: (itemId: number, updates: Record<string, any>) => void
  /** Called when user adds a new item from the inline form */
  onItemAdd?: (scorecardId: number, sectionId: number, item: { title: string; importance: string }) => void
}

// ── Helpers ────────────────────────────────────────────────────────

const importanceBadge: Record<string, { className: string; label: string }> = {
  mission_critical: { className: 'bg-destructive/10 text-destructive', label: 'KPP' },
  important: { className: 'bg-primary/10 text-primary', label: 'KSA' },
  nice_to_have: { className: 'bg-muted text-muted-foreground', label: 'APA' },
}

const scorecardTypeLabels: Record<string, string> = {
  foundational: 'Basic Requirements',
  technical: 'Technical Performance',
  manufacturing_readiness: 'Production Readiness',
  vc_metrics: 'Company Stability',
  dod_partnership: 'DoW Partnership',
}

// ── Component ──────────────────────────────────────────────────────

const ScorecardFilterPanel: React.FC<ScorecardFilterPanelProps> = ({
  scorecards,
  criteria,
  onCriteriaChange,
  onItemUpdate,
  onItemAdd,
}) => {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(
    new Set(scorecards.length > 0 ? [scorecards[0].scorecard_type] : [])
  )

  // Local overrides for immediate visual feedback (persisted via debounced onItemUpdate)
  const [importanceOverrides, setImportanceOverrides] = useState<Record<number, string>>({})
  const [categoryOverrides, setCategoryOverrides] = useState<Record<number, string[]>>({})

  // Add-item inline form state
  const [addingToScorecard, setAddingToScorecard] = useState<string | null>(null)
  const [newItemTitle, setNewItemTitle] = useState('')
  const [newItemImportance, setNewItemImportance] = useState<string>('important')
  const [newItemSectionId, setNewItemSectionId] = useState<number | null>(null)

  // Debounce backend writes so dragging doesn't flood with PATCHes (500ms)
  const debouncedItemUpdate = useDebouncedCallback(
    (itemId: number, updates: Record<string, any>) => {
      if (onItemUpdate) onItemUpdate(itemId, updates)
    },
    500
  )

  const toggleCard = (type: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const handleAddItem = (sc: FilterScorecard) => {
    if (!newItemTitle.trim() || !onItemAdd) return
    const sectionId = newItemSectionId ?? sc.sections[0]?.id
    if (!sectionId) return
    onItemAdd(sc.id, sectionId, {
      title: newItemTitle.trim(),
      importance: newItemImportance,
    })
    setNewItemTitle('')
    setNewItemImportance('important')
    setAddingToScorecard(null)
  }

  // Filter to scorecards that have items with linked specs
  const activeScorecards = scorecards.filter((sc) => sc.items.length > 0)

  if (activeScorecards.length === 0) {
    return (
      <div className="p-4 text-center">
        <SlidersHorizontal className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No scorecard criteria available.</p>
        <p className="text-xs text-muted-foreground mt-1">
          Add items to program scorecards to filter market results.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {activeScorecards.map((sc) => {
        const isExpanded = expandedCards.has(sc.scorecard_type)
        const activeItemCount = sc.items.filter(
          (item) => item.spec_id && criteria[String(item.spec_id)]?.active !== false
        ).length

        return (
          <div
            key={sc.scorecard_type}
            className="border border-border rounded-md bg-card/50 overflow-hidden"
          >
            {/* Header */}
            <button
              onClick={() => toggleCard(sc.scorecard_type)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                )}
                <span className="text-xs font-semibold text-foreground">
                  {scorecardTypeLabels[sc.scorecard_type] || sc.scorecard_type_display}
                </span>
              </div>
              <span className="text-[10px] text-muted-foreground">
                {activeItemCount}/{sc.items.length}
              </span>
            </button>

            {/* Items */}
            {isExpanded && (
              <div className="px-3 pb-3 space-y-2">
                {sc.items.map((item) => {
                  const specKey = item.spec_id ? String(item.spec_id) : `item-${item.id}`
                  const criterion = criteria[specKey]

                  return (
                    <div key={item.id} className="space-y-1">
                      {/* Item header */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-foreground truncate">
                          {item.title}
                        </span>
                        <select
                          value={importanceOverrides[item.id] ?? item.importance}
                          onChange={(e) => {
                            const newImportance = e.target.value
                            setImportanceOverrides(prev => ({ ...prev, [item.id]: newImportance }))
                            debouncedItemUpdate(item.id, { importance: newImportance })
                          }}
                          className={`text-[9px] font-medium rounded-md px-1 py-0 cursor-pointer
                            appearance-none bg-transparent border border-current/20 outline-none
                            ${importanceBadge[importanceOverrides[item.id] ?? item.importance]?.className || ''}`}
                        >
                          <option value="nice_to_have">APA</option>
                          <option value="important">KSA</option>
                          <option value="mission_critical">KPP</option>
                        </select>
                      </div>

                      {/* Filter control based on spec type */}
                      {item.spec_type === 'numeric' && item.spec_id && (() => {
                        const loVal = item.lower_threshold ?? undefined
                        const hiVal = item.upper_threshold ?? undefined

                        // Full default criterion — used both as initial value and as
                        // merge base so dragging one handle doesn't lose the other
                        const defaultCriterion = {
                          active: true,
                          importance: item.importance,
                          lower_threshold: loVal,
                          upper_threshold: hiVal,
                        }
                        const effectiveCriterion = (criterion
                          ? { ...defaultCriterion, ...criterion }
                          : defaultCriterion) as SpecCriterion

                        return (
                          <NumericCriteriaSlider
                            spec={{
                              id: item.spec_id,
                              name: item.title,
                              spec_type: 'numeric',
                              unit: item.spec_unit || '',
                              typical_min: item.typical_min,
                              typical_max: item.typical_max,
                              description: item.description,
                              data_type: '',
                              categories: [],
                            }}
                            higherIsBetter={item.higher_is_better}
                            criterion={effectiveCriterion}
                            onChange={(c) => {
                              // Merge updates onto the full effective criterion
                              const merged = { ...effectiveCriterion, ...c }
                              onCriteriaChange({
                                ...criteria,
                                [specKey]: merged,
                              })
                              // Persist threshold changes to the backend (debounced)
                              const backendUpdates: Record<string, any> = {}
                              if ('lower_threshold' in c) {
                                backendUpdates.lower_threshold = c.lower_threshold
                              }
                              if ('upper_threshold' in c) {
                                backendUpdates.upper_threshold = c.upper_threshold
                              }
                              if (Object.keys(backendUpdates).length > 0) {
                                debouncedItemUpdate(item.id, backendUpdates)
                              }
                            }}
                          />
                        )
                      })()}

                      {/* Boolean — importance badge is sufficient, no extra control needed */}
                      {item.spec_type === 'boolean' && (
                        <span className="text-[10px] text-muted-foreground">Yes / No</span>
                      )}

                      {/* Categorical toggle pills */}
                      {item.spec_type === 'categorical' && item.spec_categories && item.spec_categories.length > 0 && (() => {
                        const selected = categoryOverrides[item.id] ?? item.acceptable_categories ?? []
                        return (
                          <div className="flex flex-wrap gap-1 py-1">
                            {item.spec_categories.map((cat: any) => {
                              const value = typeof cat === 'string' ? cat : cat.value
                              const label = typeof cat === 'string' ? cat : (cat.label || cat.value)
                              const isSelected = selected.includes(value)
                              return (
                                <button
                                  key={value}
                                  onClick={() => {
                                    const updated = isSelected
                                      ? selected.filter((c: string) => c !== value)
                                      : [...selected, value]
                                    setCategoryOverrides(prev => ({ ...prev, [item.id]: updated }))
                                    debouncedItemUpdate(item.id, { acceptable_categories: updated })
                                  }}
                                  className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                                    isSelected
                                      ? 'bg-primary/10 border-primary/40 text-primary font-medium'
                                      : 'bg-muted/30 border-border text-muted-foreground hover:border-primary/30'
                                  }`}
                                >
                                  {label}
                                </button>
                              )
                            })}
                          </div>
                        )
                      })()}

                      {/* Categorical with no options defined */}
                      {item.spec_type === 'categorical' && (!item.spec_categories || item.spec_categories.length === 0) && (
                        <p className="text-[10px] text-muted-foreground italic">
                          No categories defined for this spec
                        </p>
                      )}

                      {/* Ordered categorical — read-only level bar */}
                      {item.spec_type === 'ordered_categorical' && item.spec_ordered_levels && item.spec_ordered_levels.length > 0 && (() => {
                        const sorted = [...item.spec_ordered_levels].sort((a, b) => a.order - b.order)
                        const minId = item.minimum_acceptable_level ?? null
                        const desId = item.desirable_level ?? null
                        const minIdx = sorted.findIndex((l) => l.id === minId)
                        const desIdx = sorted.findIndex((l) => l.id === desId)
                        const hib = item.higher_is_better

                        const getZoneColor = (idx: number): string => {
                          if (hib) {
                            if (minIdx >= 0 && idx < minIdx) return 'bg-destructive/20 border-destructive/40'
                            if (desIdx >= 0 && idx >= desIdx) return 'bg-emerald-500/20 border-emerald-500/40'
                            if (minIdx >= 0 && desIdx >= 0 && idx >= minIdx && idx < desIdx) return 'bg-amber-500/20 border-amber-500/40'
                          } else {
                            if (minIdx >= 0 && idx > minIdx) return 'bg-destructive/20 border-destructive/40'
                            if (desIdx >= 0 && idx <= desIdx) return 'bg-emerald-500/20 border-emerald-500/40'
                            if (minIdx >= 0 && desIdx >= 0 && idx > desIdx && idx <= minIdx) return 'bg-amber-500/20 border-amber-500/40'
                          }
                          return 'bg-muted/30 border-border'
                        }

                        return (
                          <div className="flex items-center gap-0.5 py-1">
                            {sorted.map((level, idx) => (
                              <div
                                key={level.id}
                                className={`flex-1 text-center px-0.5 py-1 rounded border text-[8px] leading-tight ${getZoneColor(idx)}`}
                                title={level.label}
                              >
                                <div className="font-medium truncate">{level.value}</div>
                              </div>
                            ))}
                          </div>
                        )
                      })()}

                      {/* Fallback for items without spec linkage */}
                      {!item.spec_type && (
                        <p className="text-[10px] text-muted-foreground italic">
                          No spec linked — qualitative only
                        </p>
                      )}
                    </div>
                  )
                })}

                {/* Add item inline form */}
                {onItemAdd && sc.sections.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/50">
                    {addingToScorecard === sc.scorecard_type ? (
                      <div className="space-y-1.5">
                        <input
                          type="text"
                          value={newItemTitle}
                          onChange={(e) => setNewItemTitle(e.target.value)}
                          placeholder="Item title..."
                          className="w-full text-xs px-2 py-1 bg-background border border-border rounded"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newItemTitle.trim()) handleAddItem(sc)
                            if (e.key === 'Escape') { setAddingToScorecard(null); setNewItemTitle('') }
                          }}
                        />
                        <div className="flex items-center gap-2">
                          {sc.sections.length > 1 && (
                            <select
                              value={newItemSectionId ?? sc.sections[0]?.id}
                              onChange={(e) => setNewItemSectionId(Number(e.target.value))}
                              className="text-[10px] px-1.5 py-0.5 border border-border rounded bg-background"
                            >
                              {sc.sections.map(s => (
                                <option key={s.id} value={s.id}>{s.title}</option>
                              ))}
                            </select>
                          )}
                          <select
                            value={newItemImportance}
                            onChange={(e) => setNewItemImportance(e.target.value)}
                            className="text-[10px] px-1.5 py-0.5 border border-border rounded bg-background"
                          >
                            <option value="nice_to_have">APA</option>
                            <option value="important">KSA</option>
                            <option value="mission_critical">KPP</option>
                          </select>
                          <button
                            onClick={() => handleAddItem(sc)}
                            disabled={!newItemTitle.trim()}
                            className="text-[10px] px-2 py-0.5 bg-primary text-primary-foreground rounded disabled:opacity-40"
                          >
                            Add
                          </button>
                          <button
                            onClick={() => { setAddingToScorecard(null); setNewItemTitle('') }}
                            className="text-[10px] text-muted-foreground hover:text-foreground"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setAddingToScorecard(sc.scorecard_type)
                          setNewItemSectionId(sc.sections[0]?.id ?? null)
                        }}
                        className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add item
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ScorecardFilterPanel
export type { FilterScorecard, FilterSection, FilterItem }
